import React from 'react'

import type { BubbleGroupPosition } from '../../../MessageAttachment/types'
import type { LinkAttachmentBaseProps } from '../../types'
import BubbleTail from '../_shared/BubbleTail'
import CardBody from '../_shared/CardBody'
import CardShell from '../_shared/CardShell'
import CardThumbnail, {
  AUDIO_BG_CLASS,
  isPlayableAudio,
  isPlayableMedia,
} from '../_shared/CardThumbnail'
import { normalizeExternalHref } from '../_shared/normalizeExternalHref'
import { useChinPalette } from '../_shared/useChinPalette'

export interface SentCardProps extends LinkAttachmentBaseProps {
  /**
   * Fired when the sender activates their own posted card. Mirrors the
   * `Received` variant so analytics fires symmetrically on both sides
   * of the conversation. Behavior depends on configuration:
   *   - **Link app with a URL**: the card chrome is an `<a target="_blank">`
   *     opening `url`; `onClick` fires alongside the navigation.
   *   - **Link app with a CTA**: the CTA owns navigation; `onClick` is
   *     ignored (the CTA's own `onClick` is the analytics hook).
   *   - **Video / audio link previews**: the shell stays non-interactive
   *     so the native media controls remain operable; `onClick` is ignored.
   */
  onClick?: () => void
  /**
   * Position of this card inside its same-author run. `'single'` / `'end'`
   * paint the accent-coloured tail; `'first'` / `'middle'` omit it because a
   * later bubble in the run owns it. Defaults to `'single'`.
   */
  groupPosition?: BubbleGroupPosition
}

/**
 * The card the sender sees in chat after a link attachment has been posted.
 * Matches the Sent column of the messaging design system in Figma — same
 * dark chrome as the Composer card minus the dismiss / edit affordances.
 *
 * The sender's own copy is clickable when a `url` is set so they can open
 * the link they just sent — symmetric with `LinkAttachment.Received`.
 */
const SentCard: React.FC<SentCardProps> = ({
  title,
  placeholderTitle,
  description,
  descriptionLines,
  url,
  mimeType,
  thumbnailUrl,
  sourceUrl,
  layout = 'featured',
  appIcon,
  cta,
  accentColor,
  status = 'ready',
  onClick,
  groupPosition = 'single',
}) => {
  // Video / audio link previews wrap the native media element — render a
  // plain non-interactive shell and let the media controls own clicks so
  // taps on play/pause/scrubber don't fire the outer card action. Same
  // rationale as `ReceivedCard`.
  const isPlayingMedia = isPlayableMedia(mimeType, sourceUrl)
  // Normalize the URL so a bare hostname like `tr.ee/briemix` is treated
  // as an external link instead of resolving against the current host.
  // Returns `undefined` for empty / whitespace-only values, so those fall
  // through to a non-navigational shell instead of producing an empty
  // `href` on the anchor.
  const normalizedUrl = normalizeExternalHref(url)
  const shellHref =
    cta == null && normalizedUrl != null && !isPlayingMedia
      ? normalizedUrl
      : undefined
  const shellOnClick =
    cta == null && !isPlayingMedia && shellHref != null ? onClick : undefined

  // A loading card is not yet an audio card: the scrape that would tell us has
  // not returned, so it keeps the ordinary 250px skeleton rather than the
  // intrinsic-height audio shell (whose `flex-1` hero would collapse to 0).
  const isAudio = status !== 'loading' && isPlayableAudio(mimeType, sourceUrl)
  const chin = useChinPalette({
    variant: 'dark',
    accentColor,
    status,
    layout,
    thumbnailUrl,
    mimeType,
    sourceUrl,
  })

  return (
    <BubbleTail
      accentHex={chin.accentHex}
      side="sender"
      // Only the last card in a same-author run carries the tail; the audio
      // player card is not a bubble, so it never does.
      visible={
        !isAudio && (groupPosition === 'single' || groupPosition === 'end')
      }
    >
      <CardShell
        variant="dark"
        href={shellHref}
        onClick={shellOnClick}
        fixedHeight={layout === 'featured' && !isAudio}
        accentHex={chin.accentHex}
        busy={chin.isLoading}
        bgClassName={isAudio ? AUDIO_BG_CLASS : undefined}
      >
        {layout === 'featured' && (
          <CardThumbnail
            variant="dark"
            thumbnailUrl={chin.thumbnailUrl}
            sourceUrl={sourceUrl}
            title={title}
            mimeType={mimeType}
            isLoading={chin.isLoading}
            onImageError={chin.onImageError}
          />
        )}
        <CardBody
          chinTextColor={chin.chinTextColor}
          variant="dark"
          ctaSurface={chin.surface}
          title={title}
          placeholderTitle={placeholderTitle}
          description={description}
          descriptionLines={descriptionLines}
          url={url}
          appIcon={appIcon}
          cta={cta}
          isLoading={chin.isLoading}
        />
      </CardShell>
    </BubbleTail>
  )
}

export default SentCard
